import { APP_WILL_MOUNT, APP_WILL_UNMOUNT } from '../base/app/actionTypes';
import { CONFERENCE_JOINED, CONFERENCE_LEFT, ENDPOINT_MESSAGE_RECEIVED } from '../base/conference/actionTypes';
import MiddlewareRegistry from '../base/redux/MiddlewareRegistry';
import { getLogger } from '../base/logging/functions';
import { CAPTION_PREFERENCES_CHANGED, SET_REQUESTING_SUBTITLES, TOGGLE_REQUESTING_SUBTITLES } from './actionTypes';
import { removeTranscriptMessage, setRequestingSubtitles, updateTranscriptMessage } from './actions.any';
import { isCaptionLanguageMatch } from './captionLanguage';
import { parseCaptionEndpointMessage } from './captionEndpointMessage';
import { CAPTION_PREFERENCES_STORAGE_KEY, DEFAULT_CAPTION_DWELL_MS, getCaptionPreferences, subscribeCaptionPreferences } from './captionPreferences';
/**
 * The type of json-message which indicates that json carries a
 * transcription result.
 */
const JSON_TYPE_TRANSCRIPTION_RESULT = 'transcription-result';
const logger = getLogger('features/subtitles');
/**
 * The type of json-message which indicates that json carries a
 * translation result.
 */
const JSON_TYPE_TRANSLATION_RESULT = 'translation-result';
/**
 * The local participant property which is used to set whether the local
 * participant wants to have a transcriber in the room.
 */
const P_NAME_REQUESTING_TRANSCRIPTION = 'requestingTranscription';
/**
 * The local participant property which is used to store the language
 * preference for translation for a participant.
 */
const P_NAME_TRANSLATION_LANGUAGE = 'translation_language';
/**
* Time after which rendered subtitles are removed when no dwell preference
* resolves (the preference itself is validated storage-safe).
*/
const REMOVE_AFTER_FALLBACK_MS = DEFAULT_CAPTION_DWELL_MS;
/**
 * Stability factor for a trancription. We'll treat a transcript as stable
 * beyond this value.
 */
const STABLE_TRANSCRIPTION_FACTOR = 0.85;
MiddlewareRegistry.register(store => {
    let unsubscribe;
    let storageTarget;
    const refreshPreferences = () => store.dispatch({ type: CAPTION_PREFERENCES_CHANGED });
    const onStorage = event => {
        if (event.key === null || event.key === CAPTION_PREFERENCES_STORAGE_KEY || event.key === 'vrs_user_info') {
            refreshPreferences();
        }
    };
    const stopListening = () => {
        unsubscribe?.();
        unsubscribe = undefined;
        storageTarget?.removeEventListener('storage', onStorage);
        storageTarget = undefined;
    };
    const clearTimers = () => {
        for (const message of store.getState()['features/subtitles']._transcriptMessages.values()) {
            if (message.clearTimeOut !== undefined)
                clearTimeout(message.clearTimeOut);
        }
    };
    return next => action => {
        switch (action.type) {
            case APP_WILL_MOUNT: {
                stopListening();
                unsubscribe = subscribeCaptionPreferences(refreshPreferences);
                // Native settings use the subscription above; browser profiles and
                // the separate TTS document also write through the storage event.
                if (typeof window !== 'undefined' && typeof document !== 'undefined') {
                    storageTarget = window;
                    storageTarget.addEventListener('storage', onStorage);
                }
                const result = next(action);
                refreshPreferences();
                return result;
            }
            case APP_WILL_UNMOUNT:
                stopListening();
                clearTimers();
                break;
            case CONFERENCE_JOINED: {
                if (store.getState()['features/subtitles']._conference !== action.conference)
                    clearTimers();
                const result = next(action);
                const conference = action.conference;
                setTimeout(() => {
                    if (store.getState()['features/base/conference'].conference === conference
                        && store.getState()['features/subtitles']._conference === conference) {
                        _maybeAutoStartSubtitles(store);
                    }
                }, 0);
                return result;
            }
            case CONFERENCE_LEFT:
                if (!action.conference || store.getState()['features/subtitles']._conference === action.conference) {
                    clearTimers();
                }
                break;
            case ENDPOINT_MESSAGE_RECEIVED:
                return _endpointMessageReceived(store, next, action);
            case TOGGLE_REQUESTING_SUBTITLES: {
                const state = store.getState()['features/subtitles'];
                const toggledValue = !state._requestingSubtitles;
                _requestingSubtitlesChange(store, toggledValue, state._language);
                break;
            }
            case SET_REQUESTING_SUBTITLES:
                _requestingSubtitlesChange(store, action.enabled, action.language);
                break;
        }
        return next(action);
    };
});
function _maybeAutoStartSubtitles({ dispatch, getState }) {
    const state = getState();
    const subtitlesState = state['features/subtitles'];
    if (subtitlesState._requestingSubtitles) {
        _requestingSubtitlesChange({ getState }, true, subtitlesState._language);
        return;
    }
    // Deaf clients must not have to discover a captions control mid-call: in
    // every real VRS room ('vrs-' prefix — the same signal the VRS layout
    // keys on) display starts automatically; elsewhere honor the stored
    // preference.
    const room = state['features/base/conference'].room;
    const isVrsRoom = typeof room === 'string' && room.startsWith('vrs-');
    if (!isVrsRoom && !getCaptionPreferences().enabledByDefault) {
        return;
    }
    dispatch(setRequestingSubtitles(true, true, subtitlesState._language || undefined));
}
/**
 * Notifies the feature transcription that the action
 * {@code ENDPOINT_MESSAGE_RECEIVED} is being dispatched within a specific redux
 * store.
 *
 * @param {Store} store - The redux store in which the specified {@code action}
 * is being dispatched.
 * @param {Dispatch} next - The redux {@code dispatch} function to
 * dispatch the specified {@code action} to the specified {@code store}.
 * @param {Action} action - The redux action {@code ENDPOINT_MESSAGE_RECEIVED}
 * which is being dispatched in the specified {@code store}.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _endpointMessageReceived({ dispatch, getState }, next, action) {
    const { data: raw } = action;
    if (![JSON_TYPE_TRANSCRIPTION_RESULT, JSON_TYPE_TRANSLATION_RESULT].includes(raw?.type)) {
        return next(action);
    }
    const json = parseCaptionEndpointMessage(raw);
    if (!json) {
        logger.warn('Ignored malformed caption message; text or message identity is unusable.');
        return next(action);
    }
    const state = getState();
    if (!state['features/base/conference'].conference)
        return next(action);
    const language = state['features/base/conference'].conference
        ?.getLocalParticipantProperty(P_NAME_TRANSLATION_LANGUAGE);
    const { dumpTranscript, skipInterimTranscriptions } = state['features/base/config'].testing ?? {};
    const transcriptMessageID = json.message_id;
    // Reject a late partial before notifying consumers or replacing its timer.
    // Completion IDs also survive display dwell when word retention is off.
    if (json.type === JSON_TYPE_TRANSCRIPTION_RESULT && json.is_interim
        && (state['features/subtitles']._finalizedTranscriptIds?.has(transcriptMessageID)
            || state['features/subtitles']._transcriptMessages.get(transcriptMessageID)?.final)) {
        return next(action);
    }
    // Dwell is a viewer preference, read per message so a mid-call change
    // applies to the next finished line without any room rejoin.
    const captionDwellMs = getCaptionPreferences().captionDwellMs;
    /*
     * TRUST BOUNDARY — caption attribution.
     *
     * The message body carries a self-declared `participant`, but any
     * conference member can send an endpoint message claiming any identity.
     * Attribution is therefore bound to the ACTUAL sender — the participant
     * the conference layer attached to this action — and the body's claim is
     * honored only in two legitimate publisher shapes:
     *
     * 1. Self-attributed captions. The human-captioner composer (manual lines
     *    and its Deepgram-relay republishes) claims its OWN participant id,
     *    with a presentation name such as "Deepgram Live Captions". When the
     *    claimed id IS the sender's id, the claimed name/avatar are accepted:
     *    a participant can already rename itself, so this grants no power it
     *    does not have.
     * 2. Speaker-attributed captions from room transcription infrastructure
     *    (jigasi), which joins as a *hidden* participant and labels each line
     *    with the id of the person SPEAKING. Hidden participants can only be
     *    admitted by deployment policy (prosody/jicofo), which is the
     *    strongest authorization signal available client-side.
     *
     * Every other mismatch renders under the sender's real identity.
     *
     * Residual trust, stated honestly: a sender may still label its own lines
     * with an arbitrary display name; hidden participants are fully trusted
     * to attribute captions to third parties (the client cannot verify who is
     * actually speaking); and message_ids remain a shared namespace, so a
     * sender can overwrite an existing line — but the overwrite now displays
     * under the overwriter's own identity instead of the original author's.
     *
     * The captioner's local echo dispatches a plain `{ id, name }` sender
     * whose id matches the claim, so it takes branch 1; remote messages carry
     * a JitsiParticipant (getId/getDisplayName/isHidden).
     */
    const sender = action.participant;
    const claimed = json.participant || {};
    const senderId = sender
        ? (typeof sender.getId === 'function' ? sender.getId() : sender.id)
        : undefined;
    const senderIsHidden = Boolean(sender)
        && typeof sender.isHidden === 'function'
        && sender.isHidden();
    const claimIsTrusted = !senderId
        || claimed.id === senderId
        || senderIsHidden;
    const participant = claimIsTrusted
        ? {
            avatarUrl: claimed.avatar_url,
            id: claimed.id,
            name: claimed.name || 'Participant'
        }
        : {
            avatarUrl: undefined,
            id: senderId,
            // Remote senders are JitsiParticipants; the redux participant
            // entry is the fallback for the rare sender without a display
            // name (reducer state is read directly — importing
            // base/participants/functions here drags the avatar/invite
            // dependency graph into every consumer of this middleware).
            name: (typeof sender.getDisplayName === 'function' && sender.getDisplayName())
                || sender.name
                || state['features/base/participants']?.remote?.get?.(senderId)?.name
                || 'Participant'
        };
    if (json.type === JSON_TYPE_TRANSLATION_RESULT && json.language === language) {
        // Displays final results in the target language if translation is
        // enabled.
        // MalkaVRS and MalkaVRI in-call captions are plain (Robert, 2026-11-01:
        // "get the video call to work smoothly, with captioning as support"):
        // no tone from the words, and none from a sender. Relay has its own
        // Expressive style, from the caller's voice.
        const newTranscriptMessage = {
            clearTimeOut: undefined,
            final: json.text,
            participant
        };
        _setClearerOnTranscriptMessage({ dispatch, getState }, transcriptMessageID, newTranscriptMessage, captionDwellMs);
        dispatch(updateTranscriptMessage(transcriptMessageID, newTranscriptMessage));
    }
    else if (json.type === JSON_TYPE_TRANSCRIPTION_RESULT) {
        // Displays interim and final results without any translation if
        // translations are disabled.
        const { text } = json.transcript[0];
        // First, notify the external API.
        if (typeof APP !== 'undefined' && !(json.is_interim && skipInterimTranscriptions)) {
            const txt = {};
            if (!json.is_interim) {
                txt.final = text;
            }
            else if (json.stability > STABLE_TRANSCRIPTION_FACTOR) {
                txt.stable = text;
            }
            else {
                txt.unstable = text;
            }
            APP.API.notifyTranscriptionChunkReceived({
                messageID: transcriptMessageID,
                language: json.language,
                participant,
                ...txt
            });
            // Dump transcript in a <transcript> element for debugging purposes.
            if (!json.is_interim && dumpTranscript) {
                try {
                    let elem = document.body.getElementsByTagName('transcript')[0];
                    if (!elem) {
                        elem = document.createElement('transcript');
                        document.body.appendChild(elem);
                    }
                    elem.append(`${new Date(json.timestamp ?? Date.now()).toISOString()} ${participant.name}: ${text}`);
                }
                catch (_) {
                    // Ignored.
                }
            }
        }
        // If the user is not requesting transcriptions just bail.
        if (!state['features/subtitles']._requestingSubtitles) {
            return next(action);
        }
        // When a caption language has been selected locally, display only
        // results matching it ('en' matches 'en-US'); without a selection,
        // display every result.
        if (!isCaptionLanguageMatch(json.language, language)) {
            return next(action);
        }
        if (json.is_interim && skipInterimTranscriptions) {
            return next(action);
        }
        // We update the previous transcript message with the same
        // message ID or adds a new transcript message if it does not
        // exist in the map.
        const existingMessage = state['features/subtitles']._transcriptMessages.get(transcriptMessageID);
        // Plain captions: no tone (see the translation result above).
        const newTranscriptMessage = {
            clearTimeOut: existingMessage?.clearTimeOut,
            language,
            participant,
            // Track metadata rides with human-captioner payloads ('hearing',
            // 'deaf', 'interpreter', 'mixed'); ASR sources omit it. Renderers
            // label only non-mixed tracks.
            track: typeof json.track === 'string' && json.track ? json.track : 'mixed'
        };
        _setClearerOnTranscriptMessage({ dispatch, getState }, transcriptMessageID, newTranscriptMessage, captionDwellMs);
        // If this is final result, update the state as a final result
        // and start a count down to remove the subtitle from the state
        if (!json.is_interim) {
            newTranscriptMessage.final = text;
        }
        else if (json.stability > STABLE_TRANSCRIPTION_FACTOR) {
            // If the message has a high stability, we can update the
            // stable field of the state and remove the previously
            // unstable results
            newTranscriptMessage.stable = text;
        }
        else {
            // Otherwise, this result has an unstable result, which we
            // add to the state. The unstable result will be appended
            // after the stable part.
            newTranscriptMessage.unstable = text;
        }
        dispatch(updateTranscriptMessage(transcriptMessageID, newTranscriptMessage));
    }
    return next(action);
}
/**
 * Toggle the local property 'requestingTranscription'. This will cause Jicofo
 * and Jigasi to decide whether the transcriber needs to be in the room.
 *
 * @param {Store} store - The redux store.
 * @param {boolean} enabled - Whether subtitles should be enabled or not.
 * @param {string} language - The language to use for translation.
 * @private
 * @returns {void}
 */
function _requestingSubtitlesChange({ getState }, enabled, language) {
    const state = getState();
    const { conference } = state['features/base/conference'];
    conference?.setLocalParticipantProperty(P_NAME_REQUESTING_TRANSCRIPTION, enabled);
    if (enabled && language) {
        conference?.setLocalParticipantProperty(P_NAME_TRANSLATION_LANGUAGE, language.replace('translation-languages:', ''));
    }
}
/**
 * Set a timeout on a TranscriptMessage object so it clears itself when it's not
 * updated.
 *
 * @param {Function} dispatch - Dispatch remove action to store.
 * @param {string} transcriptMessageID - The id of the message to remove.
 * @param {Object} transcriptMessage - The message to remove.
 * @returns {void}
 */
function _setClearerOnTranscriptMessage({ dispatch, getState }, transcriptMessageID, transcriptMessage, removeAfterMs = REMOVE_AFTER_FALLBACK_MS) {
    const subtitles = getState()['features/subtitles'];
    const existing = subtitles._transcriptMessages.get(transcriptMessageID);
    if (existing?.clearTimeOut !== undefined)
        clearTimeout(existing.clearTimeOut);
    const conference = subtitles._conference;
    transcriptMessage.clearTimeOut = window.setTimeout(() => {
        const current = getState()['features/subtitles'];
        if (current._conference === conference
            && current._transcriptMessages.get(transcriptMessageID) === transcriptMessage) {
            dispatch(removeTranscriptMessage(transcriptMessageID));
        }
    }, removeAfterMs);
}
